import { BillingDetailsForm, invoiceLocaleName } from '@/components/billing/BillingDetailsForm'
import { AddCardDialog, AddedPaymentMethod } from '@/components/payment-methods/AddCardDialog'
import { EditNicknameDialog } from '@/components/payment-methods/EditNicknameDialog'
import {
	describePaymentMethod,
	InvoicePaymentMethodRow,
	PaymentMethodsList,
} from '@/components/payment-methods/PaymentMethodsList'
import { RequireAccess } from '@/components/require-access'
import { useJobillaOrgId } from '@/hooks'
import { useAuth, usePrivateChannel } from '@/lib'
import {
	BuildingLibraryIcon,
	CheckIcon,
	ClipboardDocumentIcon,
	ExclamationTriangleIcon,
	InformationCircleIcon,
	PlusIcon,
} from '@heroicons/react/24/outline'
import { CheckBadgeIcon } from '@heroicons/react/24/solid'
import {
	type BillingProfileData,
	broadcastChannel,
	PaymentMethodData,
	paymentMethodEvent,
	paymentMethodIndexQueryKey,
	preferredPaymentMethod,
	profileEvent,
	profilesIndexQueryKey,
	useInvoiceSettingsShow,
	usePaymentMethodDestroy,
	usePaymentMethodIndex,
	usePaymentMethodPrimary,
	usePaymentMethodSetNickname,
	useProfilesArchive,
	useProfilesIndex,
	useProfilesInvoicePrimary,
	useProfilesPrimary,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import { Loader2Icon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	appendToastAtom,
	ConfirmationDialog,
	type ConfirmationDialogRef,
	FormDialog,
	logger,
	SubtleLoader,
	Tooltip,
	useToastStoreContext,
} from '../../../../ui'
import { Button } from '../../../rapid-ui'
import { SettingsNav } from '../settings-nav'
import { SettingsSubheading } from '../shared'
import { SubscriptionSection } from './subscription'

const EM_DASH = '—'

const POLL_INTERVAL_MS = 2_000
const POLL_DURATION_MS = 20_000

/** Expand an ISO country code to a localized country name, falling back to the raw code. */
function formatCountry(code: string | null | undefined, locale: string): string | null {
	if (!code) {
		return null
	}
	try {
		return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code
	} catch {
		return code
	}
}

/** Turn a Stripe tax-id type code into a readable label, e.g. `au_abn` -> "AU ABN", `eu_vat` -> "EU VAT". */
function formatTaxIdType(type: string): string {
	return type.split('_').map((part) => part.toUpperCase()).join(' ')
}

/**
 * Flag for a Jobilla billing entity. Inline SVGs for the entities we operate
 * (FI, DE) so it renders consistently everywhere (emoji flags don't on Windows);
 * any other code falls back to the bare country code.
 */
function EntityFlag({ country }: { country: string }) {
	const code = country.toUpperCase()

	const svg = code === 'FI'
		? (
			<svg viewBox="0 0 18 11" className="h-full w-full">
				<rect width="18" height="11" fill="#fff" />
				<rect y="4" width="18" height="3" fill="#003580" />
				<rect x="5" width="3" height="11" fill="#003580" />
			</svg>
		)
		: code === 'DE'
		? (
			<svg viewBox="0 0 5 3" className="h-full w-full">
				<rect width="5" height="3" fill="#000" />
				<rect y="1" width="5" height="1" fill="#DD0000" />
				<rect y="2" width="5" height="1" fill="#FFCE00" />
			</svg>
		)
		: null

	if (!svg) {
		return <span className="text-xs font-medium text-grey-500">{code}</span>
	}

	return (
		<span
			className="inline-block h-3.5 w-[22px] overflow-hidden rounded-[2px] ring-1 ring-black/10"
			aria-label={code}
		>
			{svg}
		</span>
	)
}

function CopyButton({ value, label }: { value: string; label: string }) {
	const { t } = useTranslation('common')
	const [copied, setCopied] = useState(false)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
	}, [])

	const handleCopy = () => {
		void navigator.clipboard?.writeText(value).catch(() => {})
		setCopied(true)
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
		timeoutRef.current = setTimeout(() => setCopied(false), 1400)
	}

	return (
		<button
			type="button"
			onClick={handleCopy}
			aria-label={t('profiles.bankTransfer.copyField', { field: label })}
			className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-grey-200 text-grey-500 hover:text-brand-700 hover:border-grey-300 transition-colors cursor-pointer"
		>
			{copied
				? <CheckIcon className="w-4 h-4 text-green-600" />
				: <ClipboardDocumentIcon className="w-4 h-4" />}
		</button>
	)
}

function BankDetailRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
	return (
		<div className="grid grid-cols-[110px_1fr_auto] items-center gap-3 py-3 border-b border-grey-100 last:border-b-0">
			<span className="text-sm text-grey-500">{label}</span>
			<span className={`text-sm text-grey-900 min-w-0 break-all ${mono ? 'font-mono' : ''}`}>{value}</span>
			<CopyButton value={value} label={label} />
		</div>
	)
}

function BankTransferDetailsDialog(
	{ profile, onClose }: { profile: BillingProfileData | null; onClose: () => void },
) {
	const { t, i18n } = useTranslation('common')

	return (
		<FormDialog
			open={profile !== null}
			onClose={onClose}
			title={t('profiles.bankTransfer.title')}
			maxWidth="md"
		>
			{profile !== null && (
				<div className="space-y-4">
					<p className="-mt-2 text-sm text-grey-500">
						{t('profiles.bankTransfer.subtitle', { name: profile.name ?? profile.stripeCustomerId })}
					</p>

					{profile.fundingInstructions.map((instruction) => (
						<div
							key={`${instruction.country}-${instruction.iban}`}
							className="rounded-lg border border-grey-200 bg-grey-50/50 px-4"
						>
							<BankDetailRow
								label={t('profiles.bankTransfer.accountHolder')}
								value={instruction.accountHolder}
							/>
							<BankDetailRow label={t('profiles.bankTransfer.iban')} value={instruction.iban} mono />
							<BankDetailRow label={t('profiles.bankTransfer.bic')} value={instruction.bic} mono />
							<div className="grid grid-cols-[110px_1fr_auto] items-center gap-3 py-3">
								<span className="text-sm text-grey-500">{t('profiles.bankTransfer.country')}</span>
								<span className="flex items-center gap-2 text-sm text-grey-900">
									<EntityFlag country={instruction.country} />
									{formatCountry(instruction.country, i18n.language) ?? instruction.country}
								</span>
								<span />
							</div>
						</div>
					))}

					<p className="text-xs text-grey-500">{t('profiles.bankTransfer.footnote')}</p>
				</div>
			)}
		</FormDialog>
	)
}

export function Billing() {
	const { t, i18n } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const { can } = useAuth()
	const queryClient = useQueryClient()
	const toastStore = useToastStoreContext()
	const appendToast = useSetAtom(appendToastAtom, { store: toastStore })

	const { data, isLoading, isError, refetch } = useProfilesIndex(organisationId, {
		enabled: !!organisationId && can('profile:view'),
	})

	const invoiceSettings = useInvoiceSettingsShow(organisationId, {
		enabled: !!organisationId && can('invoice:view'),
	})
	const externalInvoicing = invoiceSettings.data?.externalInvoicing ?? false
	const canPayViaInvoice = invoiceSettings.data?.canPayViaInvoice ?? false

	const canViewPaymentMethods = can('payment_method:view')
	const canCreatePaymentMethod = can('payment_method:create')
	const canSetDefaultPaymentMethod = can('payment_method:primary')
	const canUpdatePaymentMethod = can('payment_method:update')
	const canDeletePaymentMethod = can('payment_method:delete')

	// Card management is unavailable to externally-invoiced organisations — they
	// settle via bank transfer, so there are no cards to manage.
	const showPaymentMethods = canViewPaymentMethods && !externalInvoicing

	// Keep the list live when a profile is edited in the Stripe portal, made
	// primary, or archived — including from another tab or user.
	usePrivateChannel(
		organisationId && can('profile:view')
			? broadcastChannel.OrganisationProfiles({ organisation: organisationId })
			: null,
		{
			[profileEvent.Updated]: [profilesIndexQueryKey],
			[profileEvent.PrimaryChanged]: [profilesIndexQueryKey],
			[profileEvent.Archived]: [profilesIndexQueryKey],
		},
	)

	// Keep the card list live when a payment method is added, made primary, or
	// removed — including from another tab, another user, or the Stripe portal.
	usePrivateChannel(
		organisationId && showPaymentMethods
			? broadcastChannel.OrganisationPaymentMethod({ organisation: organisationId })
			: null,
		{
			[paymentMethodEvent.Updated]: [paymentMethodIndexQueryKey],
			[paymentMethodEvent.PrimaryChanged]: [paymentMethodIndexQueryKey],
			[paymentMethodEvent.Deleted]: [paymentMethodIndexQueryKey],
		},
	)

	const setPrimaryMutation = useProfilesPrimary({
		onSuccess: () => refetch(),
	})

	const archiveMutation = useProfilesArchive({
		onSuccess: () => refetch(),
	})

	const confirmDialogRef = useRef<ConfirmationDialogRef>(null)
	const [bankProfile, setBankProfile] = useState<BillingProfileData | null>(null)
	const [editingProfile, setEditingProfile] = useState<BillingProfileData | 'new' | null>(null)

	// Adding a card: which profile the open dialog targets, plus post-add polling
	// until the new method surfaces in the mirror (and its nickname, if any, lands).
	const [addCardProfileId, setAddCardProfileId] = useState<string | null>(null)
	const [pollUntil, setPollUntil] = useState<number | null>(null)
	const [pollingProfileId, setPollingProfileId] = useState<string | null>(null)
	const [pendingNickname, setPendingNickname] = useState<AddedPaymentMethod | null>(null)
	const [editingNickname, setEditingNickname] = useState<PaymentMethodData | null>(null)
	const isPolling = pollUntil !== null

	const {
		data: paymentMethods,
		isLoading: paymentMethodsLoading,
		isFetching: paymentMethodsFetching,
	} = usePaymentMethodIndex(organisationId, undefined, {
		enabled: !!organisationId && showPaymentMethods,
		refetchInterval: isPolling ? POLL_INTERVAL_MS : false,
	})

	const primaryPaymentMutation = usePaymentMethodPrimary({
		mutationKey: ['payment-method-primary'],
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: paymentMethodIndexQueryKey(organisationId) })
			queryClient.invalidateQueries({ queryKey: profilesIndexQueryKey(organisationId) })
		},
		onError: (err) => {
			logger.error(err, { tags: { component: 'Billing', action: 'set-primary-payment-method' } })
			appendToast({
				title: t('settings.paymentMethods.setDefaultFailed'),
				description: err?.response?.data?.message,
				variant: 'error',
				cause: err,
			})
		},
	})

	const invoicePrimaryMutation = useProfilesInvoicePrimary({
		mutationKey: ['profile-invoice-primary'],
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: profilesIndexQueryKey(organisationId) })
			queryClient.invalidateQueries({ queryKey: paymentMethodIndexQueryKey(organisationId) })
		},
		onError: (err) => {
			logger.error(err, { tags: { component: 'Billing', action: 'set-invoice-primary' } })
			appendToast({
				title: t('settings.paymentMethods.setDefaultFailed'),
				description: err?.response?.data?.message,
				variant: 'error',
				cause: err,
			})
		},
	})

	const destroyPaymentMutation = usePaymentMethodDestroy({
		mutationKey: ['payment-method-destroy'],
		onSuccess: () => queryClient.invalidateQueries({ queryKey: paymentMethodIndexQueryKey(organisationId) }),
		onError: (err) => {
			logger.error(err, { tags: { component: 'Billing', action: 'destroy-payment-method' } })
			appendToast({
				title: t('settings.paymentMethods.delete.failed'),
				description: err?.response?.data?.message,
				variant: 'error',
				cause: err,
			})
		},
	})

	const setNicknameMutation = usePaymentMethodSetNickname({
		mutationKey: ['payment-method-set-nickname'],
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: paymentMethodIndexQueryKey(organisationId) })
			setEditingNickname(null)
		},
		onError: (err) => {
			logger.error(err, { tags: { component: 'Billing', action: 'set-payment-method-nickname' } })
			appendToast({
				title: t('settings.paymentMethods.setNicknameFailed'),
				description: err?.response?.data?.message,
				variant: 'error',
				cause: err,
			})
		},
	})

	useEffect(() => {
		if (pollUntil === null) {
			return
		}
		const remaining = pollUntil - Date.now()
		if (remaining <= 0) {
			setPollUntil(null)
			setPollingProfileId(null)
			return
		}
		const timer = window.setTimeout(() => {
			setPollUntil(null)
			setPollingProfileId(null)
		}, remaining)
		return () => window.clearTimeout(timer)
	}, [pollUntil])

	const paymentRows = paymentMethods?.data ?? []

	// Once the newly-added PM appears in the mirror, send the nickname update.
	useEffect(() => {
		if (!pendingNickname || pendingNickname.nickname === null) {
			return
		}
		const match = paymentRows.find((pm) => pm.stripePaymentMethodId === pendingNickname.stripePaymentMethodId)
		if (!match) {
			return
		}
		setNicknameMutation.mutate({
			organisation: organisationId,
			paymentMethod: match.id,
			data: { nickname: pendingNickname.nickname },
		})
		setPendingNickname(null)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paymentRows, pendingNickname])

	// If polling expires without the PM showing up, drop the pending nickname and warn.
	useEffect(() => {
		if (pollUntil !== null || !pendingNickname || pendingNickname.nickname === null) {
			return
		}
		appendToast({
			title: t('settings.paymentMethods.setNicknameFailed'),
			variant: 'error',
		})
		setPendingNickname(null)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pollUntil])

	const handleCardAdded = (added: AddedPaymentMethod) => {
		setPollUntil(Date.now() + POLL_DURATION_MS)
		setPollingProfileId(addCardProfileId)
		if (added.nickname !== null) {
			setPendingNickname(added)
		}
		setAddCardProfileId(null)
	}

	const handleSetDefaultPaymentMethod = (paymentMethod: PaymentMethodData) => {
		primaryPaymentMutation.mutate({ organisation: organisationId, paymentMethod: paymentMethod.id })
	}

	const handleSaveNickname = (paymentMethod: PaymentMethodData, nickname: string | null) => {
		if (nickname === (paymentMethod.nickname ?? null)) {
			setEditingNickname(null)
			return
		}
		setNicknameMutation.mutate({
			organisation: organisationId,
			paymentMethod: paymentMethod.id,
			data: { nickname },
		})
	}

	const handleDeletePaymentMethod = async (paymentMethod: PaymentMethodData) => {
		const description = describePaymentMethod(paymentMethod, t)
		try {
			await confirmDialogRef.current?.confirm({
				title: t('settings.paymentMethods.delete.title'),
				message: t('settings.paymentMethods.delete.message', { description }),
				confirmText: t('settings.paymentMethods.delete.confirm'),
				cancelText: t('settings.paymentMethods.delete.cancel'),
			})
		} catch {
			return
		}

		destroyPaymentMutation.mutate({ organisation: organisationId, paymentMethod: paymentMethod.id })
	}

	const isPaymentRowMutating = (paymentMethodId: string): boolean =>
		[primaryPaymentMutation, destroyPaymentMutation, setNicknameMutation].some((mutation) =>
			mutation.isPending && mutation.variables?.paymentMethod === paymentMethodId
		)

	const profiles = data?.data ?? []
	const primaryProfile = profiles.find((profile) => profile.isPrimary)
	const otherProfiles = profiles.filter((profile) => !profile.isPrimary)
	const orderedProfiles = primaryProfile ? [primaryProfile, ...otherProfiles] : otherProfiles
	const billingEntity = data?.billingEntity ?? null

	// Methods attached to a customer that isn't among the listed profiles (e.g. an
	// archived one) still need a home, so they fall into a trailing "Other" group.
	const profileCustomerIds = new Set(profiles.map((profile) => profile.stripeCustomerId))
	const leftoverMethods = paymentRows.filter(
		(method) => !method.stripeCustomerId || !profileCustomerIds.has(method.stripeCustomerId),
	)

	// External-invoicing organisations are limited to a single billing profile.
	const addBlockedByExternalInvoicing = externalInvoicing && profiles.length >= 1

	const handleArchive = async (profile: (typeof profiles)[number]) => {
		try {
			await confirmDialogRef.current?.confirm({
				title: t('profiles.archiveConfirm.title'),
				message: t('profiles.archiveConfirm.message', {
					name: profile.name ?? profile.stripeCustomerId,
				}),
				confirmText: t('profiles.archiveConfirm.confirm'),
				cancelText: t('profiles.archiveConfirm.cancel'),
			})
		} catch {
			return
		}

		archiveMutation.mutate({ organisation: organisationId, profile: profile.id })
	}

	const renderPaymentSection = (
		methods: PaymentMethodData[],
		profileId: string | null,
		invoiceProfile: BillingProfileData | null,
	) => {
		const isCheckingForUpdates = isPolling && paymentMethodsFetching && pollingProfileId === profileId
		const invoiceIsPrimary = invoiceProfile?.preferredPaymentMethod === preferredPaymentMethod.invoice
		const rows = invoiceIsPrimary ? methods.map((method) => ({ ...method, isDefault: false })) : methods
		const isInvoiceMutating = invoicePrimaryMutation.isPending
			&& invoicePrimaryMutation.variables?.profile === invoiceProfile?.id

		const invoiceRow = invoiceProfile
			? (
				<InvoicePaymentMethodRow
					isPrimary={invoiceIsPrimary}
					canSetDefault={canSetDefaultPaymentMethod}
					onSetDefault={() =>
						invoicePrimaryMutation.mutate({ organisation: organisationId, profile: invoiceProfile.id })}
					isMutating={isInvoiceMutating}
				/>
			)
			: null

		return (
			<div className="mt-8 pt-4">
				<div className="flex items-center justify-between gap-3 mb-2">
					<div className="flex items-center gap-2">
						<h4 className="text-sm font-semibold uppercase tracking-wider text-grey-800">
							{t('settings.paymentMethods.cardsHeading')}
						</h4>
						{isCheckingForUpdates && (
							<Tooltip content={t('settings.paymentMethods.polling')}>
								<Loader2Icon
									className="size-3.5 text-grey-400 animate-spin"
									aria-label={t('settings.paymentMethods.polling')}
								/>
							</Tooltip>
						)}
					</div>
				</div>

				{invoiceIsPrimary ? invoiceRow : null}

				{paymentMethodsLoading
					? <SubtleLoader delay={0} />
					: rows.length === 0
					? (invoiceProfile
						? null
						: <p className="text-sm text-grey-500">{t('settings.paymentMethods.noCards')}</p>)
					: (
						<PaymentMethodsList
							rows={rows}
							canSetDefault={canSetDefaultPaymentMethod}
							canUpdate={canUpdatePaymentMethod}
							canDelete={canDeletePaymentMethod}
							onSetDefault={handleSetDefaultPaymentMethod}
							onEditNickname={setEditingNickname}
							onDelete={handleDeletePaymentMethod}
							isRowMutating={isPaymentRowMutating}
						/>
					)}

				{invoiceIsPrimary ? null : invoiceRow}
			</div>
		)
	}

	const renderProfile = (profile: (typeof profiles)[number]) => {
		const addressLines = profile.address
			? [
				profile.address.line1,
				profile.address.line2,
				[profile.address.postalCode, profile.address.city].filter(Boolean).join(' '),
				formatCountry(profile.address.country, i18n.language),
			].filter((line): line is string => Boolean(line))
			: []

		// Disabled: the backend "make primary" just repoints organisation.stripe_id to
		// another Stripe customer, stranding the existing subscription/entitlements on the
		// old one. Needs backend reconciliation before it's safe to expose.
		// To restore: const canMakePrimary = !profile.is_primary && !profile.archived && can('profile:set-primary')
		const canMakePrimary = false
		const canEdit = can('profile:edit')
		const canArchive = !profile.isPrimary && can('profile:archive')
		const hasActions = canMakePrimary || canEdit || canArchive

		const primaryBadge = profile.isPrimary
			? (
				<span className="flex items-center gap-1.5 text-xs font-medium text-brand-700 flex-shrink-0">
					<span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
					{t('profiles.primary')}
				</span>
			)
			: null

		const actionButtons = (
			<div className="flex items-center gap-2 flex-wrap">
				{canMakePrimary && (
					<button
						type="button"
						onClick={() =>
							setPrimaryMutation.mutate({
								organisation: organisationId,
								profile: profile.id,
							})}
						disabled={setPrimaryMutation.isPending}
						className="text-xs font-medium text-grey-700 border border-grey-200 hover:border-grey-300 hover:bg-grey-50 px-2.5 py-1 rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-grey-200 disabled:hover:bg-transparent whitespace-nowrap"
					>
						{setPrimaryMutation.isPending && setPrimaryMutation.variables?.profile === profile.id
							? t('profiles.makingPrimary')
							: t('profiles.makePrimary')}
					</button>
				)}
				{canEdit && (
					<button
						type="button"
						onClick={() => setEditingProfile(profile)}
						className="text-xs font-medium text-grey-700 border border-grey-200 hover:border-grey-300 hover:bg-grey-50 px-2.5 py-1 rounded-md transition-colors cursor-pointer whitespace-nowrap"
					>
						{t('profiles.edit')}
					</button>
				)}
				{canArchive && (
					<button
						type="button"
						onClick={() => handleArchive(profile)}
						disabled={archiveMutation.isPending}
						className="text-xs font-medium text-red-600 border border-grey-200 hover:border-red-300 hover:bg-red-50 px-2.5 py-1 rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-grey-200 disabled:hover:bg-transparent whitespace-nowrap"
					>
						{archiveMutation.isPending && archiveMutation.variables?.profile === profile.id
							? t('profiles.archiving')
							: t('profiles.archive')}
					</button>
				)}
			</div>
		)

		const profileMethods = profile.stripeCustomerId
			? paymentRows.filter((method) => method.stripeCustomerId === profile.stripeCustomerId)
			: []

		return (
			<div key={profile.id} className="group border border-grey-200 rounded-lg pt-5 min-w-0 space-y-5">
				<div className="px-5 space-y-5">
					<div className="flex items-start justify-between gap-3 pb-3 border-b border-grey-100">
						<div className="font-medium text-grey-900 min-w-0 wrap-break-word">
							{profile.name ?? profile.stripeCustomerId}
						</div>
						<div className="flex items-center gap-2 shrink-0">
							{primaryBadge}
							{hasActions && actionButtons}
						</div>
					</div>

					{setPrimaryMutation.isError && setPrimaryMutation.variables?.profile === profile.id && (
						<p className="mt-2 text-sm text-red-600">{t('profiles.makePrimaryError')}</p>
					)}

					{archiveMutation.isError && archiveMutation.variables?.profile === profile.id && (
						<p className="mt-2 text-sm text-red-600">{t('profiles.archiveError')}</p>
					)}

					<div className="mt-4 border-b border-grey-100">
						<h4 className="text-sm font-semibold uppercase tracking-wider text-grey-800 mb-2">
							{t('profiles.detailsHeading')}
						</h4>
						<dl className="divide-y divide-grey-100 text-sm">
							<div className="flex items-center justify-between gap-4 py-3">
								<dt className="text-grey-500">{t('profiles.email')}</dt>
								<dd className="text-grey-900 min-w-0 wrap-break-word text-right">
									{profile.email ?? EM_DASH}
								</dd>
							</div>
							<div className="flex items-start justify-between gap-4 py-3">
								<dt className="text-grey-500">{t('profiles.address')}</dt>
								<dd className="text-grey-900 min-w-0 wrap-break-word text-right">
									{addressLines.length > 0
										? addressLines.map((line, index) => <div key={`${index}-${line}`}>{line}</div>)
										: EM_DASH}
								</dd>
							</div>
							<div className="flex items-start justify-between gap-4 py-3">
								<dt className="text-grey-500">{t('profiles.taxIds')}</dt>
								<dd className="text-grey-900 min-w-0 wrap-break-word">
									{profile.taxIds.length > 0
										? (
											<div className="space-y-1">
												{profile.taxIds.map((taxId) => (
													<div
														key={`${taxId.type}-${taxId.value}`}
														className="flex items-center flex-wrap justify-end gap-1.5 min-w-0"
													>
														<span className="text-xs font-medium text-grey-500">
															{formatTaxIdType(taxId.type)}
														</span>
														<span className="break-all">{taxId.value}</span>
														{taxId.verification === 'verified' && (
															<span className="flex items-center gap-0.5 text-xs text-green-600">
																<CheckBadgeIcon className="w-4 h-4" />
																{t('profiles.vatVerified')}
															</span>
														)}
													</div>
												))}
											</div>
										)
										: EM_DASH}
								</dd>
							</div>
							<div className="flex items-center justify-between gap-4 py-3">
								<dt className="text-grey-500">{t('billingDetails.invoiceLanguage')}</dt>
								<dd className="text-grey-900 min-w-0 wrap-break-word text-right">
									{invoiceLocaleName(profile.locale)}
								</dd>
							</div>
						</dl>
					</div>

					{showPaymentMethods
						&& renderPaymentSection(profileMethods, profile.id, canPayViaInvoice ? profile : null)}
				</div>
				{/*	Footer section */}
				<div className="flex items-center justify-between gap-2 rounded-b-lg border-t border-grey-200 px-5 py-5 animate-gradient from-gray-50 to-gray-100 bg-gradient-to-r">
					<div>
						{profile.fundingInstructions.length > 0 && (
							<button
								type="button"
								onClick={() => setBankProfile(profile)}
								className="inline-flex items-center gap-1.5 text-xs font-medium text-grey-700 border border-grey-200 hover:border-grey-300 hover:bg-grey-50 px-2.5 py-1 rounded-md transition-colors cursor-pointer"
							>
								<BuildingLibraryIcon className="w-4 h-4" />
								{t('profiles.bankTransfer.button')}
							</button>
						)}
					</div>
					{showPaymentMethods && (
						<Tooltip
							content={t('settings.paymentMethods.adminOnly')}
							disabled={canCreatePaymentMethod}
						>
							{/* @TODO: Replace with @/UI button component */}
							<button
								type="button"
								onClick={() => setAddCardProfileId(profile.id)}
								disabled={!canCreatePaymentMethod}
								className="flex items-center gap-1.5 rounded-md border border-grey-200 bg-white px-2.5 py-1 text-xs font-medium text-grey-700 transition-colors hover:border-brand-500 hover:text-brand-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-grey-200 disabled:hover:text-grey-700"
							>
								<PlusIcon className="h-3.5 w-3.5" />
								{t('settings.paymentMethods.addCard.button')}
							</button>
						</Tooltip>
					)}
				</div>
			</div>
		)
	}

	return (
		<SettingsNav active="billing" fullWidth={false}>
			<RequireAccess
				permission="billing:read"
				fallback={
					<section className="mt-8 pr-8">
						<p className="text-red-600">{t('profiles.noPermission')}</p>
					</section>
				}
			>
				<section className="space-y-8 mt-8 pr-8 max-w-[1100px]">
					<SubscriptionSection />

					<div className="flex items-end justify-between gap-4">
						<div className="flex items-baseline align-middle gap-3">
							<SettingsSubheading>{t('profiles.title')}</SettingsSubheading>

							{!isLoading && !isError && profiles.length > 0 && can('profile:edit') && (
								<Tooltip
									content={addBlockedByExternalInvoicing ? t('profiles.externalInvoicingLimit') : ''}
									disabled={!addBlockedByExternalInvoicing}
								>
									<button
										type="button"
										onClick={() => setEditingProfile('new')}
										disabled={addBlockedByExternalInvoicing}
										className="flex items-center gap-1.5 rounded-md border border-grey-200 bg-white px-2.5 py-1 text-xs font-medium text-grey-700 transition-colors hover:border-brand-500 hover:text-brand-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-grey-200 disabled:hover:text-grey-700"
									>
										<PlusIcon className="h-3.5 w-3.5" />
										{t('profiles.addNew')}
									</button>
								</Tooltip>
							)}
						</div>

						{billingEntity && (
							<div className="text-right">
								<div className="text-[10px] font-medium text-grey-500 uppercase tracking-wider">
									{t('profiles.billedBy')}
								</div>
								<div className="mt-1 flex items-center justify-end gap-1.5">
									<span className="text-sm font-medium text-grey-900">
										{billingEntity.name}
									</span>
									<EntityFlag country={billingEntity.country} />
								</div>
							</div>
						)}
					</div>

					{isLoading && <SubtleLoader delay={0} />}

					{!isLoading && isError && (
						<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
							<div className="flex items-start gap-3">
								<ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
								<div>
									<div className="font-medium text-amber-900 mb-1">{t('profiles.loadError')}</div>
									<div className="text-sm text-amber-700 mb-3">{t('profiles.loadErrorDesc')}</div>
									<Button type={'neutral'} onClick={() => refetch()}>
										{t('common.tryAgain')}
									</Button>
								</div>
							</div>
						</div>
					)}

					{!isLoading && !isError && profiles.length === 0 && (
						<div className="border border-grey-200 rounded-lg p-10 text-center">
							<InformationCircleIcon className="w-8 h-8 text-grey-400 mx-auto mb-3" />
							<div className="font-medium text-grey-900">{t('profiles.empty')}</div>

							{can('profile:edit') && (
								<div className="mt-5 flex justify-center">
									<Button
										type={'neutral'}
										onClick={() => setEditingProfile('new')}
									>
										{t('profiles.create')}
									</Button>
								</div>
							)}
						</div>
					)}

					{!isLoading && !isError && profiles.length > 0 && (
						<div className="space-y-4">
							{orderedProfiles.map((profile) => renderProfile(profile))}

							{showPaymentMethods && leftoverMethods.length > 0 && (
								<div className="border border-grey-200 rounded-lg p-5 min-w-0">
									<div className="font-medium text-grey-900">
										{t('settings.paymentMethods.otherProfile')}
									</div>
									{renderPaymentSection(leftoverMethods, null, null)}
								</div>
							)}
						</div>
					)}

					<ConfirmationDialog ref={confirmDialogRef} />

					<BankTransferDetailsDialog profile={bankProfile} onClose={() => setBankProfile(null)} />

					<FormDialog
						open={editingProfile !== null}
						onClose={() => setEditingProfile(null)}
						title={editingProfile === 'new' ? t('profiles.createTitle') : t('profiles.editTitle')}
						maxWidth="md"
					>
						{editingProfile !== null && (
							<BillingDetailsForm
								organisationId={organisationId}
								profile={editingProfile === 'new' ? null : editingProfile}
								submitLabel={t('profiles.save')}
								onSaved={() => setEditingProfile(null)}
							/>
						)}
					</FormDialog>

					<AddCardDialog
						organisationId={organisationId}
						open={addCardProfileId !== null}
						profileId={addCardProfileId ?? undefined}
						onClose={() => setAddCardProfileId(null)}
						onCardAdded={handleCardAdded}
					/>

					<EditNicknameDialog
						paymentMethod={editingNickname}
						onClose={() => setEditingNickname(null)}
						onSave={handleSaveNickname}
						isSaving={setNicknameMutation.isPending
							&& setNicknameMutation.variables?.paymentMethod === editingNickname?.id}
					/>
				</section>
			</RequireAccess>
		</SettingsNav>
	)
}
